/**
 * /dashboard/posh/training — POSH training module list.
 *
 * Migration note (Phase 3f — 2026-06-01): the per-module scenario player
 * + quiz routes (/dashboard/posh/training/$moduleId, /quiz) are now
 * ported. Card CTAs swapped from <a href> to typed Link.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { useMemo } from "react";
import { Award, Clock, GraduationCap, RotateCcw, Upload } from "lucide-react";
import { localize } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { useCertificates, useTrainingModules } from "@/hooks/use-posh";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { PoshIcon } from "@/components/posh/posh-icons";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { fmtDate as fmtDueDate } from "@/lib/posh-format";
import { scenarioProgressLabel } from "@/lib/posh-employee";
import { usePoshLang } from "@/lib/posh-lang";
import { poshText, type PoshTextKey } from "@/components/posh/posh-ui-text";
import { TrainingLanguageSwitch } from "@/components/posh/training-language-switch";
import { TrainingErrorAlert } from "@/components/posh/training-error-alert";
import { quizUnlocked } from "@/components/posh/training-player-logic";
import type { ProgressStatus } from "@/types/posh";

// Status badge labels in the course language.
const PROGRESS_STATUS_TEXT: Record<ProgressStatus, PoshTextKey> = {
  not_started: "statusNotStarted",
  in_progress: "statusInProgress",
  completed: "statusCompleted",
  expired: "statusExpired",
};

// The backend renews a completed module early when the quiz is passed
// within 30 days of its lapse date (POSHRefresherNoticeDays).
const RENEW_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export const Route = createFileRoute("/_authgated/app/$tenantSlug/posh/training/")({
  component: TrainingListRoute,
});

/**
 * Honest per-module completion %: completed scenarios over the module's
 * live scenario total. `scenarios_completed` may carry ids that no longer
 * exist in the module (content reseeds), so the value is clamped to 100 —
 * never show >100%. A module with no scenarios reports 0 (guard the
 * divide-by-zero).
 */
export function honestTrainingPercent(
  completedCount: number,
  scenarioTotal: number,
): number {
  if (scenarioTotal <= 0) return 0;
  return Math.min(100, Math.round((100 * completedCount) / scenarioTotal));
}

function ListSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-10 w-10 rounded-lg" />
            <Skeleton className="mt-3 h-5 w-2/3" />
            <Skeleton className="mt-2 h-4 w-full" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-2 w-full" />
          </CardContent>
          <CardFooter>
            <Skeleton className="h-9 w-28" />
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function TrainingList() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const hrAdmin = isHRAdmin(user?.role);
  const modulesQ = useTrainingModules();
  const certsQ = useCertificates();
  const { lang } = usePoshLang();

  const certByModule = useMemo(() => {
    const map = new Map<number, boolean>();
    for (const c of certsQ.data?.certificates ?? []) {
      // Only a CURRENT certificate earns the "Certified" badge — a lapsed
      // one (annual refresher, migration 121) is history. An older
      // backend sends no `state`: fall back to the revoked flag.
      const current = c.state ? c.state === "valid" : !c.is_revoked;
      if (current) map.set(c.module_id, true);
    }
    return map;
  }, [certsQ.data]);

  if (modulesQ.isLoading) return <ListSkeleton />;

  if (modulesQ.isError) {
    return (
      <TrainingErrorAlert
        title={poshText("loadModulesError", lang)}
        error={modulesQ.error}
        lang={lang}
        onRetry={() => void modulesQ.refetch()}
      />
    );
  }

  const modules = [...(modulesQ.data?.modules ?? [])].sort(
    (a, b) => a.module.sort_order - b.module.sort_order,
  );

  if (modules.length === 0) {
    return (
      <Card lang={lang}>
        <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
            <GraduationCap className="h-6 w-6" />
          </span>
          <p className="max-w-sm text-sm text-muted-foreground">
            {poshText("modulesEmpty", lang)}
          </p>
          <Button
            variant="outline"
            className="mt-1 min-h-11 gap-1.5 sm:min-h-10"
            onClick={() => void modulesQ.refetch()}
          >
            <RotateCcw aria-hidden className="h-4 w-4" />
            {poshText("retry", lang)}
          </Button>
          {hrAdmin && (
            <Button asChild variant="outline" className="min-h-11 gap-1.5 sm:min-h-10">
              <Link to="/app/$tenantSlug/bulk-imports/new" params={{ tenantSlug }}>
                <Upload aria-hidden className="h-4 w-4" />
                {poshText("bulkAssign", lang)}
              </Link>
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div lang={lang} className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {modules.map(({ module, progress, scenario_total, refresher_due_on }) => {
        const hasCert = certByModule.get(module.id) ?? false;
        const status = progress?.status ?? "not_started";
        // Annual refresher: a completed module inside the 30-day notice
        // window can be renewed early by passing the quiz again.
        const dueMs = refresher_due_on ? new Date(refresher_due_on).getTime() : NaN;
        const renewSoon =
          status === "completed" &&
          Number.isFinite(dueMs) &&
          dueMs - Date.now() <= RENEW_WINDOW_MS;
        // Honest % from real counts (2026-08 remediation round 2): the
        // DTO now carries scenario_total, so the bar reflects
        // scenarios actually completed — never the old status-based
        // 100/50/0 synthesis. Clamped ≤100 because scenarios_completed
        // can hold ids removed by a content reseed.
        const completedCount = progress?.scenarios_completed?.length ?? 0;
        const scenarioTotal = scenario_total ?? 0;
        const percent =
          status === "completed"
            ? 100
            : honestTrainingPercent(completedCount, scenarioTotal);
        // Quiz unlocks only once every scenario is done (or the module
        // is already completed — keeps Review access after a reseed
        // grows the scenario list). The page copy promises "work
        // through each scenario, then pass the quiz".
        const canQuiz = quizUnlocked(status, completedCount, scenarioTotal);
        return (
          <Card key={module.id} className="flex flex-col">
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                  <PoshIcon name={module.icon} className="h-5 w-5" />
                </span>
                <div className="flex flex-col items-end gap-1.5">
                  <StatusBadge
                    status={status}
                    label={poshText(PROGRESS_STATUS_TEXT[status] ?? "statusNotStarted", lang)}
                  />
                  {hasCert && (
                    <Badge className="gap-1 bg-green-100 text-green-800 hover:bg-green-100">
                      <Award className="h-3 w-3" />
                      {poshText("certified", lang)}
                    </Badge>
                  )}
                </div>
              </div>
              <CardTitle className="mt-3 text-base">
                {localize(module.title, lang) || module.code}
              </CardTitle>
              <CardDescription>
                {localize(module.description, lang)}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 space-y-3">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {status === "completed"
                      ? poshText("completed", lang)
                      : status === "expired"
                        ? poshText("lapsed", lang)
                        : scenarioTotal > 0
                        ? scenarioProgressLabel(completedCount, scenarioTotal, lang)
                        : poshText("noScenariosYet", lang)}
                  </span>
                  <span className="tabular-nums">{percent}%</span>
                </div>
                <Progress
                  value={percent}
                  className="h-2"
                  aria-label={poshText("progressAria", lang, {
                    title: localize(module.title, lang) || module.code,
                    p: percent,
                  })}
                />
              </div>
              {renewSoon && refresher_due_on && (
                <p className="text-xs text-amber-800">
                  {poshText("refresherDueBy", lang, {
                    date: fmtDueDate(refresher_due_on),
                  })}
                </p>
              )}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  {poshText("minutes", lang, { n: module.duration_minutes })}
                </span>
                {module.is_mandatory && (
                  <span className="ml-1 rounded bg-accent px-1.5 py-0.5 text-xs font-medium text-accent-foreground">
                    {poshText("mandatory", lang)}
                  </span>
                )}
              </div>
            </CardContent>
            <CardFooter className="gap-2">
              <Button variant="outline" asChild className="flex-1">
                <Link
                  to="/app/$tenantSlug/posh/training/$moduleId"
                  params={{tenantSlug, moduleId: String(module.id)}}
                >
                  {poshText(
                    status === "completed"
                      ? renewSoon
                        ? "renew"
                        : "review"
                      : status === "in_progress"
                        ? "continue"
                        : status === "expired"
                          ? "startRefresher"
                          : "start",
                    lang,
                  )}
                </Link>
              </Button>
              {canQuiz ? (
                <Button asChild variant="outline">
                  <Link
                    to="/app/$tenantSlug/posh/training/$moduleId/quiz"
                    params={{tenantSlug, moduleId: String(module.id)}}
                  >
                    {poshText("quiz", lang)}
                  </Link>
                </Button>
              ) : (
                // A disabled asChild Button would still navigate via the
                // Link — render a real disabled button instead.
                <Button
                  variant="outline"
                  disabled
                  title={poshText("finishScenariosFirst", lang)}
                >
                  {poshText("quiz", lang)}
                </Button>
              )}
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
}

function TrainingListRoute() {
  const { lang } = usePoshLang();
  return (
    <PoshModuleShell
      title={poshText("pageTitle", lang)}
      description={poshText("pageDescription", lang)}
      actions={<TrainingLanguageSwitch />}
    >
      <PoshFeatureGate>
        <TrainingList />
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
